import React, { FC, useContext } from 'react';
import { Box, Checkbox, Link, Tooltip, Typography, useTheme } from '@mui/material';
import StarRating from '../StarRating/StarRating';
import { InfoOutlined, OpenInNew } from '@mui/icons-material';
import { constants } from '../../constants/constants';
import { useTranslation } from 'react-i18next';
import Helper from '../../shared/helpers/Helper';
import Note from '../Notes/Note';
import { AuthContext } from '../../providers/AuthProvider';
import DeleteReview from './DeleteReview';
import PlatformService from '../../services/PlatformService';
import { IReviewDateFormat, IReviewNewProps } from '../../shared/interfaces/IReviewNew';
import { Simulate } from 'react-dom/test-utils';
import input = Simulate.input;
import { IReviewMetadata } from '../../services/CommentAiService';

interface Props extends IReviewNewProps {
  rating: number;
  originalRating: number;
  hide: boolean;
  dateFormat?: IReviewDateFormat;
  reviewLink?: string;
  reviewFor?: string;
  AIInput?: string | null | undefined;
  noReviewText: boolean;
  reviewMeta: IReviewMetadata;
}

const ReviewHeader: FC<Props> = ({
  reviewEntry,
  originalRating,
  rating,
  commentingModule,
  communityManagement,
  AIInput,
  noReviewText,
  hide,
  deletionModule,
  dateFormat,
  reviewLink,
  reviewFor,
  reviewMeta,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { hasRole } = useContext(AuthContext);

  if (hide) {
    return null;
  }

  if (typeof deletionModule == 'undefined') {
    deletionModule = !!commentingModule;
  }

  function formatDate(timestamp: string | null | undefined, short: boolean) {
    if (!dateFormat?.review) {
      return Helper.getFormattedDate(timestamp, short);
    }
    return Helper.getCustomFormattedDate(timestamp, dateFormat.review);
  }

  reviewEntry.selectionMeta = {
    AIInput: AIInput,
    noReviewText: noReviewText,
    communityManagement: communityManagement,
    reviewMetaData: reviewMeta,
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', mt: theme.spacing(1) }}>
      {reviewEntry.onSelectionChange && (
        <Checkbox
          checked={!!reviewEntry.selectionChecked}
          onChange={(event) => reviewEntry.onSelectionChange?.(event.target.checked)}
          sx={{ mr: theme.spacing(0.5) }}
        />
      )}
      <StarRating
        value={rating}
        sx={{ pl: theme.spacing(2) }}
      />
      <Box sx={{ display: 'flex', ml: theme.spacing(1), cursor: 'pointer' }}>
        <Tooltip
          title={`${t('info_rating')}: ${rating}`}
          placement="top"
          arrow
        >
          <InfoOutlined
            fontSize="small"
            sx={{ color: theme.palette.primary.main }}
          />
        </Tooltip>
      </Box>
      <img
        src={constants.platform_logos[reviewEntry.platform]}
        alt={`${reviewEntry.platform} Logo`}
        style={{
          maxHeight: '32px',
          maxWidth: '76px',
          marginLeft: theme.spacing(1),
        }}
      />
      {reviewFor && (
        <Tooltip
          title={`${t('reviewed_doctor')}:`}
          placement="top"
          arrow
        >
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              cursor: 'pointer',
              ml: theme.spacing(1),
            }}
          >
            {reviewFor}
          </Typography>
        </Tooltip>
      )}
      {(reviewEntry.timestamp || reviewLink) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            ml: 'auto',
            pr: 1,
          }}
        >
          {reviewEntry.deleted && hasRole('can-use-deleted-reviews') && (
            <Box marginRight={theme.spacing(4)}>
              <Tooltip
                title={
                  PlatformService.deletedCanBeOutdated(reviewEntry.platform)
                    ? t('review_deleted_or_outdated')
                    : t('review_deleted')
                }
                arrow
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.error.main,
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 700,
                    fontFamily: 'Roboto, sans-serif',
                  }}
                  aria-disabled
                >
                  {t('deleted')}
                </Typography>
              </Tooltip>
            </Box>
          )}
          {reviewEntry.isUpdated && (
            <Box marginRight={theme.spacing(4)}>
              <Typography
                variant="caption"
                sx={{
                  color: '#F7B419',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 700,
                  fontFamily: 'Roboto, sans-serif',
                }}
                aria-disabled
              >
                {t('updated')}
              </Typography>
            </Box>
          )}
          {hasRole('can-use-note') && (
            <Note
              note={reviewEntry.note}
              id={reviewEntry.id}
              type="comment"
            />
          )}
          {reviewEntry.timestamp && (
            <>
              {reviewEntry.review.updatedAt ? (
                <Tooltip
                  title={
                    <>
                      Erstellt: {formatDate(reviewEntry.timestamp, false)}
                      <br />
                      Aktualisiert: {formatDate(reviewEntry.review.updatedAt, false)}
                    </>
                  }
                  arrow
                >
                  <Typography
                    variant="body2"
                    sx={{ cursor: 'pointer' }}
                  >
                    {formatDate(reviewEntry.review.updatedAt, true)}
                  </Typography>
                </Tooltip>
              ) : (
                <Tooltip
                  title={formatDate(reviewEntry.timestamp, false)}
                  arrow
                >
                  <Typography
                    variant="body2"
                    sx={{ cursor: 'pointer' }}
                  >
                    {formatDate(reviewEntry.timestamp, true)}
                  </Typography>
                </Tooltip>
              )}
            </>
          )}
          {deletionModule && hasRole('can-use-deletion') && (
            <DeleteReview
              id={reviewEntry.id}
              deletion={reviewEntry.deletion}
            />
          )}
          {reviewLink && (
            <Tooltip
              title={t('to_review')}
              arrow
            >
              <Link
                href={reviewLink}
                underline="none"
                target="_blank"
                rel="noreferrer"
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <OpenInNew />
              </Link>
            </Tooltip>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ReviewHeader;
